import { IAPICodeDTO, IApiSuccessWrapper } from '$/types'
import { apiSlice } from 'app/services/api'
import { CBVThemes } from 'features/DataManagement/Components/ColorByValue/utils/cbvThemes'

/**
 * Expect endpoint to be something like:
 * /api/colorbyvalue/{id}?featureName={featureType}
 *
 * | featureType               | id                        |
 * | ------------------------- | ------------------------- |
 * | dataManagementDefault     | attribute id              |
 * | dataManagementCustom      | custom data view id       |
 * | qraCurent                 | N/A                       |
 * | qraSavedState             | saved state id            |
 * | hmca                      | N/A                       |
 */
export enum CBVSupportedViewType {
  dataManagementDefaultAttributesView = 'dmStandard',
  dataManagementCustomAttributesView = 'dmCustom',
  qraCurrentState = 'qraCurrentState',
  qraSavedState = 'qraSavedState',
  hmca = 'hmca'
}

interface ICBVRequestParam {
  feature: CBVSupportedViewType
  id?: string
}

export interface IColorByValueAttributeSegmentColorProfile {
  color: string
  value: {
    string?: IAPICodeDTO
    number?: { min: number; max: number }
    date?: { minDate: string; maxDate: string }
    boolean?: boolean
  }
}

export interface IColorByValueAttributeMap {
  /**
   * Can be used as a `selectedAttributeId`.
   */
  attributeId: string
  /**
   * Table column field name..
   */
  attributeCode: string
  attributeName: string
  attributeType: 'string' | 'number' | 'boolean' | 'date'
  attributeTypeRange: {
    string?: IAPICodeDTO[] // For maintaining order
    number?: { min: number; max: number }
    date?: { minDate: string; maxDate: string }
    // boolean is obvious
  }
  iconTheme: CBVThemes
  configurations: IColorByValueAttributeSegmentColorProfile[]
}

export interface IColorByValueConfig {
  /**
   * Versioning to anticipate/handle data structure change
   * Inspired by Redux persist config
   */
  version?: number
  /**
   * Coummunicate API load status
   * Managed locally, server side copy disregarded.
   * init: initial tempalte data (awaiting data from server)
   * done: receieved response from server (with or w/o actual data)
   * fail: unexpected situation..
   */
  loadStatus: 'init' | 'done' | 'fail'
  /**
   * Helps to decide on Update/PUT API usage based on whether the
   * value is populated.
   */
  putId?: string
  /**
   * Table / attribute group id
   */
  viewType: CBVSupportedViewType
  viewId?: string
  viewName: string
  /**
   * At most one attribute can be selected at a time
   */
  selectedAttributeId: string | null
  /**
   * map of id to configuration object w/ id
   */
  attributesMap: IColorByValueAttributeMap[]
}

export const colorByValueApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['ColorByValue']
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getColorByValueConfig: build.query<IColorByValueConfig, ICBVRequestParam>(
        {
          query: (params) => ({
            url: `/colourbyvalue/${params.id}`,
            params: {
              featureName: params.feature
            }
          }),
          transformResponse: (
            res: IApiSuccessWrapper<{ id: string; configuration: string }>
          ) => {
            const config = JSON.parse(
              res.result.configuration
            ) as IColorByValueConfig

            config.putId = res.result.id

            return config
          },
          providesTags: (_res, _err, params) => [
            { type: 'ColorByValue', id: JSON.stringify(params) }
          ]
        }
      ),
      upsertColorByValueConfig: build.mutation<
        IColorByValueConfig,
        Partial<IColorByValueConfig>
      >({
        query: (cbvConfig) => ({
          url: '/colourbyvalue',
          method: cbvConfig.putId != null ? 'PUT' : 'POST',
          body: {
            id: cbvConfig.putId,
            feature: cbvConfig.viewType,
            tableId: cbvConfig.viewId,
            configuration: JSON.stringify(cbvConfig)
          }
        }),
        transformResponse: (res: IApiSuccessWrapper) => {
          return res.result as IColorByValueConfig
        },
        invalidatesTags: (_res, _err, cbvConfig) => [
          {
            type: 'ColorByValue',
            id: JSON.stringify({
              feature: cbvConfig.viewType,
              id: cbvConfig.viewId
            } as ICBVRequestParam)
          }
        ]
      })
    })
  })

export const {
  useGetColorByValueConfigQuery,
  useUpsertColorByValueConfigMutation
} = colorByValueApi
